import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAssetStore } from '../store/assetStore';
import { VendorAsset } from '../lib/api';
import { toast } from 'sonner';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'hidden', label: 'Hidden' },
  { value: 'rejected', label: 'Rejected' },
];

function Assets() {
  const {
    assets,
    total,
    isLoading,
    error,
    searchQuery,
    selectedCategory,
    selectedStatus,
    fetchAssets,
    setSearchQuery,
    setCategory,
    setStatus,
    hideAsset,
    unhideAsset,
    submitAsset,
    deleteProduct,
  } = useAssetStore();

  const [searchInput, setSearchInput] = useState(searchQuery);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  useEffect(() => {
    fetchAssets().catch((e: Error) => toast.error(e.message));
  }, [fetchAssets, searchQuery, selectedCategory, selectedStatus]);

  const submitSearch = () => {
    setSearchQuery(searchInput.trim());
  };

  const categories = Array.from(new Set(assets.map((a) => a.category).filter(Boolean)));

  const handleHide = async (asset: VendorAsset) => {
    try {
      await (asset.status === 'hidden' ? unhideAsset(asset.id) : hideAsset(asset.id));
      toast.success(asset.status === 'hidden' ? 'Asset unhidden' : 'Asset hidden');
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const handleSubmit = async (asset: VendorAsset) => {
    try {
      await submitAsset(asset.id);
      toast.success('Asset submitted for review');
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const handleDelete = async (asset: VendorAsset) => {
    if (!window.confirm(`Delete "${asset.name}" permanently?`)) return;
    setDeletingId(asset.id);
    try {
      await deleteProduct(asset.id);
      toast.success('Asset deleted');
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="container">
      <div
        className="header"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '1.5rem',
          position: 'static',
          padding: 0,
          background: 'transparent',
          border: 'none',
        }}
      >
        <h1 className="page-title">My Assets</h1>
        <Link to="/assets/new" className="btn btn--primary">
          + New Asset
        </Link>
      </div>
      <p className="page-subtitle">
        {total} asset{total === 1 ? '' : 's'} total.
      </p>

      <div className="toolbar">
        <input
          type="text"
          placeholder="Search assets…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submitSearch()}
        />
        <select
          value={selectedCategory || ''}
          onChange={(e) => setCategory(e.target.value || null)}
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select value={selectedStatus || ''} onChange={(e) => setStatus(e.target.value || null)}>
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <button type="button" className="btn btn--secondary" onClick={submitSearch}>
          Search
        </button>
      </div>

      {error && <p className="form-error">{error}</p>}

      {isLoading && assets.length === 0 ? (
        <div className="loading-spinner">
          <div className="spinner" />
        </div>
      ) : assets.length === 0 ? (
        <div className="panel">
          <div className="empty-state">
            <p>No assets found.</p>
            <Link to="/assets/new" className="btn btn--primary">
              Upload your first asset
            </Link>
          </div>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Category</th>
                <th>Price</th>
                <th>Status</th>
                <th>Versions</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {assets.map((asset) => (
                <tr key={asset.id}>
                  <td>
                    <Link to={`/assets/${asset.id}`}>{asset.name}</Link>
                    {asset.rejection_reason && (
                      <div className="rejection-note">Rejected: {asset.rejection_reason}</div>
                    )}
                  </td>
                  <td>{asset.category}</td>
                  <td>€{asset.price.toFixed(2)}</td>
                  <td>
                    <span className={`status-badge status--${asset.status}`}>{asset.status}</span>
                  </td>
                  <td>{asset.versions?.length ?? 0}</td>
                  <td>
                    <div className="actions">
                      <Link to={`/assets/${asset.id}`} className="btn btn--sm btn--secondary">
                        Edit
                      </Link>
                      {asset.status === 'rejected' && (
                        <button
                          type="button"
                          className="btn btn--sm btn--outline"
                          onClick={() => handleSubmit(asset)}
                        >
                          Resubmit
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn--sm btn--outline"
                        onClick={() => handleHide(asset)}
                      >
                        {asset.status === 'hidden' ? 'Unhide' : 'Hide'}
                      </button>
                      <button
                        type="button"
                        className="btn btn--sm btn--danger"
                        disabled={deletingId === asset.id}
                        onClick={() => handleDelete(asset)}
                      >
                        {deletingId === asset.id ? 'Deleting…' : 'Delete'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default Assets;
